import React, { useState } from 'react';
import { Building2, Users, CalendarDays, FileText, MessageSquare, Boxes } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';

interface Option {
  value: string;
  label: string;
  description: string;
}

interface StepProps {
  result: {
    ui_step: string;
    options: Option[];
  };
  onSelect: (value: string, label: string) => void;
}

const ICONS: Record<string, React.ReactNode> = {
  accounts: <Building2 className="h-5 w-5" />,
  users: <Users className="h-5 w-5" />,
  companies: <Building2 className="h-5 w-5" />,
  meetings: <CalendarDays className="h-5 w-5" />,
  docs: <FileText className="h-5 w-5" />,
  conversations: <MessageSquare className="h-5 w-5" />,
  conversation_messages: <MessageSquare className="h-5 w-5" />,
};

const EntitySelectorStep: React.FC<StepProps> = ({ result, onSelect }) => {
  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (option: Option) => {
    setSelected(option.value);
    onSelect(option.value, option.label);
  };

  return (
    <div className="w-full max-w-lg">
      <p className="text-sm font-medium text-gray-700 mb-3">
        What would you like to monitor?
      </p>
      <div className="grid grid-cols-2 gap-3">
        {result.options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option)}
            disabled={selected !== null}
            className={cn(
              'flex flex-col items-start gap-2 p-4 rounded-lg border text-left transition-all',
              selected === option.value
                ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500'
                : selected !== null
                ? 'border-gray-200 bg-white opacity-50 cursor-not-allowed'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
            )}
          >
            <div
              className={cn(
                'p-1.5 rounded-md',
                selected === option.value ? 'text-violet-600 bg-violet-100' : 'text-gray-500 bg-gray-100'
              )}
            >
              {ICONS[option.value] || <Boxes className="h-5 w-5" />}
            </div>
            <div>
              <p className={cn('text-sm font-medium', selected === option.value ? 'text-violet-700' : 'text-gray-800')}>
                {option.label}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default EntitySelectorStep;
